/**
 * Commitment Store
 *
 * Manages first-class commitment artifacts and their item links.
 * DB-first: all durable state lives in Supabase; in-memory is a working cache.
 *
 * Coverage is computed server-side via get_commitment_coverage RPC and cached
 * here. It refreshes on every fetchFromSupabase call (page mount + after any
 * link/unlink action).
 */

import { create } from 'zustand';
import type {
  Commitment,
  CommitmentItemLink,
  CommitmentCoverageSummary,
  CommitmentWithCoverage,
  CreateCommitmentInput,
  UpdateCommitmentInput,
  CommitmentRow,
  CommitmentItemLinkRow,
  CommitmentCoverageRow,
} from '../../types/prioritization/commitment';
import {
  commitmentRowToDomain,
  commitmentLinkRowToDomain,
  commitmentCoverageRowToDomain,
} from '../../types/prioritization/commitment';
import {
  getCurrentWorkspaceId,
  getSafeSupabaseClient,
  createInitialSyncState,
  type SyncState,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';

const COMMITMENTS_TABLE = 'commitments';
const LINKS_TABLE = 'commitment_item_links';

// ── State shape ───────────────────────────────────────────────────────────────

interface CommitmentState {
  commitments: Commitment[];
  links: CommitmentItemLink[];
  coverage: CommitmentCoverageSummary[];
  syncState: SyncState;

  fetchFromSupabase: () => Promise<void>;

  createCommitment: (input: CreateCommitmentInput) => Promise<Commitment | null>;
  /**
   * Why the most recent createCommitment returned null (cleared on success),
   * so callers can show the actual refusal instead of silently doing nothing.
   */
  lastCreateError: string | null;
  updateCommitment: (id: string, input: UpdateCommitmentInput) => Promise<void>;
  deleteCommitment: (id: string) => Promise<void>;

  linkItem: (commitmentId: string, itemId: string) => Promise<boolean>;
  unlinkItem: (commitmentId: string, itemId: string) => Promise<void>;

  /** Derived: merges commitments + links + coverage into a single display-ready shape */
  getCommitmentsWithCoverage: () => CommitmentWithCoverage[];
  getLinkedCommitmentsForItem: (itemId: string) => CommitmentWithCoverage[];
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useCommitmentStore = create<CommitmentState>((set, get) => ({
  commitments: [],
  links: [],
  coverage: [],
  syncState: createInitialSyncState(),
  lastCreateError: null,

  // ── Fetch ───────────────────────────────────────────────────────────────────

  fetchFromSupabase: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set((s) => ({ syncState: { ...s.syncState, isLoading: true } }));

    try {
      const [commitmentsRes, linksRes, coverageRes] = await Promise.all([
        supabase
          .from(COMMITMENTS_TABLE)
          .select('*')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false }),
        supabase
          .from(LINKS_TABLE)
          .select('*')
          .eq('workspace_id', workspaceId),
        supabase.rpc('get_commitment_coverage', { p_workspace_id: workspaceId }),
      ]);

      if (commitmentsRes.error) {
        console.error('[CommitmentStore] Failed to fetch commitments:', commitmentsRes.error.message);
      }
      if (linksRes.error) {
        console.error('[CommitmentStore] Failed to fetch links:', linksRes.error.message);
      }
      if (coverageRes.error) {
        console.error('[CommitmentStore] Failed to fetch coverage:', coverageRes.error.message);
      }

      set({
        commitments: ((commitmentsRes.data as CommitmentRow[] | null) ?? []).map(commitmentRowToDomain),
        links: ((linksRes.data as CommitmentItemLinkRow[] | null) ?? []).map(commitmentLinkRowToDomain),
        coverage: ((coverageRes.data as CommitmentCoverageRow[] | null) ?? []).map(commitmentCoverageRowToDomain),
        syncState: { ...get().syncState, isLoading: false, lastSyncAt: new Date() },
      });
    } catch (error) {
      console.error('[CommitmentStore] fetchFromSupabase failed:', error);
      set((s) => ({ syncState: { ...s.syncState, isLoading: false } }));
    }
  },

  // ── Create (via audit RPC) ───────────────────────────────────────────────────

  createCommitment: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase) {
      set({ lastCreateError: 'Database not available — the commitment was not created.' });
      return null;
    }
    if (!workspaceId) {
      set({ lastCreateError: 'No workspace selected — the commitment was not created.' });
      return null;
    }

    let actorId: string | null = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      actorId = user?.id ?? null;
    } catch {
      // non-blocking
    }
    if (!actorId) {
      set({ lastCreateError: 'You are not signed in — the commitment was not created.' });
      return null;
    }

    const { data, error } = await supabase.rpc('create_commitment', {
      p_workspace_id:    workspaceId,
      p_actor_id:        actorId,
      p_title:           input.title,
      p_commitment_type: input.commitmentType,
      p_status:          input.status ?? 'active',
      p_priority:        input.priority ?? 'medium',
      p_description:     input.description ?? null,
      p_due_date:        input.dueDate ?? null,
      p_owner_id:        input.ownerId ?? null,
      p_owner_name:      input.ownerName ?? null,
      p_counterparty:    input.counterparty ?? null,
    });

    if (error || !data?.success) {
      const msg = error?.message ?? data?.error ?? 'unknown error';
      console.error('[CommitmentStore] createCommitment RPC failed:', msg);
      dispatchStoreError('commitmentStore', 'createCommitment', msg);
      set({ lastCreateError: msg });
      return null;
    }

    const created = commitmentRowToDomain(data.commitment as CommitmentRow);
    set((s) => ({ commitments: [created, ...s.commitments], lastCreateError: null }));
    return created;
  },

  // ── Update ──────────────────────────────────────────────────────────────────
  // Two acts, two server functions (§2.3a). A status transition is
  // update_commitment_status (360/735); every other field edit — title,
  // description, type, priority, due date, owner, counterparty — is
  // update_commitment (772), which records a commitment_updated governance
  // event. The direct UPDATE this used to do is closed: it could also have set
  // `status` and skipped the audit trail entirely.

  updateCommitment: async (id, input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    // ── Status transition → audit RPC ────────────────────────────────────────
    if (input.status !== undefined) {
      let actorId: string | null = null;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        actorId = user?.id ?? null;
      } catch { /* non-blocking */ }

      if (actorId) {
        const { data: rpcData, error: rpcError } = await supabase.rpc('update_commitment_status', {
          p_commitment_id: id,
          p_actor_id:      actorId,
          p_new_status:    input.status,
          p_rationale:     null,
        });

        if (rpcError || !rpcData?.success) {
          const msg = rpcError?.message ?? rpcData?.error ?? 'unknown error';
          console.error('[CommitmentStore] update_commitment_status RPC failed:', msg);
          dispatchStoreError('commitmentStore', 'updateCommitment', msg);
          return;
        }

        const updatedFromRpc = commitmentRowToDomain(rpcData.commitment as CommitmentRow);
        set((s) => ({
          commitments: s.commitments.map((c) => (c.id === id ? updatedFromRpc : c)),
        }));

        // Apply any remaining non-status field changes below
        const { status: _s, ...restInput } = input;
        if (Object.keys(restInput).length === 0) return;
        input = restInput as UpdateCommitmentInput;
      }
    }

    // ── Detail edits → update_commitment (772) ───────────────────────────────
    const fields: Record<string, unknown> = {};
    if (input.title !== undefined)          fields.title           = input.title;
    if (input.description !== undefined)    fields.description     = input.description;
    if (input.commitmentType !== undefined) fields.commitment_type = input.commitmentType;
    if (input.priority !== undefined)       fields.priority        = input.priority;
    if ('dueDate' in input)                 fields.due_date        = input.dueDate;
    if ('ownerId' in input)                 fields.owner_id        = input.ownerId;
    if ('ownerName' in input)               fields.owner_name      = input.ownerName;
    if ('counterparty' in input)            fields.counterparty    = input.counterparty;

    if (Object.keys(fields).length === 0) return; // nothing to do

    const { data, error } = await supabase.rpc('update_commitment', {
      p_commitment_id: id,
      p_fields: fields,
    });

    if (error || !data?.success || !data.commitment) {
      const msg = error?.message ?? data?.error ?? 'unknown error';
      console.error('[CommitmentStore] update_commitment RPC failed:', msg);
      dispatchStoreError('commitmentStore', 'updateCommitment', msg);
      return;
    }

    const updated = commitmentRowToDomain(data.commitment as CommitmentRow);
    set((s) => ({
      commitments: s.commitments.map((c) => (c.id === id ? updated : c)),
    }));
  },

  // ── Delete (via audit RPC) ───────────────────────────────────────────────────

  deleteCommitment: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    let actorId: string | null = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      actorId = user?.id ?? null;
    } catch { /* non-blocking */ }
    if (!actorId) return;

    const { data, error } = await supabase.rpc('delete_commitment', {
      p_commitment_id: id,
      p_actor_id:      actorId,
      p_rationale:     null,
    });

    if (error || !data?.success) {
      const msg = error?.message ?? data?.error ?? 'unknown error';
      console.error('[CommitmentStore] delete_commitment RPC failed:', msg);
      dispatchStoreError('commitmentStore', 'deleteCommitment', msg);
      return;
    }

    set((s) => ({
      commitments: s.commitments.filter((c) => c.id !== id),
      links: s.links.filter((l) => l.commitmentId !== id),
      coverage: s.coverage.filter((cv) => cv.commitmentId !== id),
    }));
  },

  // ── Link / Unlink (via audit RPCs) ──────────────────────────────────────────

  linkItem: async (commitmentId, itemId) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return false;

    let actorId: string | null = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      actorId = user?.id ?? null;
    } catch { /* non-blocking */ }
    if (!actorId) return false;

    const { data, error } = await supabase.rpc('link_commitment_item', {
      p_commitment_id: commitmentId,
      p_item_id:       itemId,
      p_workspace_id:  workspaceId,
      p_actor_id:      actorId,
    });

    if (error || !data?.success) {
      console.error('[CommitmentStore] link_commitment_item RPC failed:', error?.message ?? data?.error);
      return false;
    }

    // Refresh full state so coverage reflects the new link
    await get().fetchFromSupabase();
    return true;
  },

  unlinkItem: async (commitmentId, itemId) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    let actorId: string | null = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      actorId = user?.id ?? null;
    } catch { /* non-blocking */ }
    if (!actorId) return;

    const { data, error } = await supabase.rpc('unlink_commitment_item', {
      p_commitment_id: commitmentId,
      p_item_id:       itemId,
      p_actor_id:      actorId,
    });

    if (error || !data?.success) {
      console.error('[CommitmentStore] unlink_commitment_item RPC failed:', error?.message ?? data?.error);
      return;
    }

    // Refresh full state so coverage reflects the removed link
    await get().fetchFromSupabase();
  },

  // ── Derived getters ──────────────────────────────────────────────────────────

  getCommitmentsWithCoverage: () => {
    const { commitments, links, coverage } = get();
    const coverageMap = new Map(coverage.map((cv) => [cv.commitmentId, cv]));
    const linksMap = new Map<string, string[]>();
    for (const link of links) {
      const existing = linksMap.get(link.commitmentId) ?? [];
      existing.push(link.itemId);
      linksMap.set(link.commitmentId, existing);
    }

    return commitments.map((c): CommitmentWithCoverage => {
      const cv = coverageMap.get(c.id);
      const linkedItemIds = linksMap.get(c.id) ?? [];
      return {
        ...c,
        linkedItemIds,
        coveredItemIds: cv?.coveredItemIds ?? [],
        coverage: cv?.coverageStatus ?? 'no-items',
        coveredItemCount: cv?.coveredItemCount ?? 0,
        linkedItemCount: cv?.linkedItemCount ?? linkedItemIds.length,
      };
    });
  },

  getLinkedCommitmentsForItem: (itemId) => {
    const { links } = get();
    const commitmentIds = new Set(
      links.filter((l) => l.itemId === itemId).map((l) => l.commitmentId)
    );
    return get()
      .getCommitmentsWithCoverage()
      .filter((c) => commitmentIds.has(c.id));
  },
}));
